import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Home, ArrowLeft, TrendingUp, TrendingDown, Euro, Landmark,
  ChevronLeft, ChevronRight, BarChart3, PieChart, FileText,
  CreditCard, Banknote, Users, ShoppingCart, Building
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function FinancialSummaryPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // month ou year
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (token) {
      loadSummary();
    }
  }, [token, selectedPeriod, period]);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const periodParam = period === 'year' ? selectedPeriod.slice(0, 4) : selectedPeriod;
      const response = await axios.get(
        `${API}/api/monthly-charges/summary/${periodParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSummary(response.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const navigatePeriod = (direction) => {
    const [year, month] = selectedPeriod.split('-').map(Number);
    
    if (period === 'year') {
      setSelectedPeriod(`${year + direction}-${month.toString().padStart(2, '0')}`);
    } else {
      let newMonth = month + direction;
      let newYear = year;
      
      if (newMonth > 12) {
        newMonth = 1;
        newYear++;
      } else if (newMonth < 1) {
        newMonth = 12;
        newYear--;
      }
      
      setSelectedPeriod(`${newYear}-${newMonth.toString().padStart(2, '0')}`);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatPeriodLabel = () => {
    const [year, month] = selectedPeriod.split('-');
    const months = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    
    if (period === 'year') {
      return `Année ${year}`;
    }
    return `${months[parseInt(month)]} ${year}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => {
              sessionStorage.setItem('reports_previous_path', '/financial-summary');
              navigate('/reports');
            }} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Rapports
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <PieChart className="w-6 h-6 text-indigo-400" />
              Résumé Financier
            </h1>
          </div>
          <Button 
            onClick={() => {
              sessionStorage.removeItem('reports_authenticated');
              navigate('/');
            }} 
            variant="ghost" 
            size="sm" 
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
          >
            <Home className="w-4 h-4 mr-1" />
            Quitter Rapports
          </Button>
        </div>

        {/* Sélecteur de période */}
        <div className="flex items-center justify-between mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigatePeriod(-1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-xl font-semibold text-white min-w-[200px] text-center">
              {formatPeriodLabel()}
            </div>
            <Button onClick={() => navigatePeriod(1)} variant="ghost" size="sm" className="text-white/70">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setPeriod('month')}
              size="sm"
              className={period === 'month' 
                ? 'bg-indigo-500 hover:bg-indigo-600' 
                : 'bg-white/10 hover:bg-white/20 text-white'
              }
            >
              Mensuel
            </Button>
            <Button
              onClick={() => setPeriod('year')}
              size="sm"
              className={period === 'year' 
                ? 'bg-indigo-500 hover:bg-indigo-600' 
                : 'bg-white/10 hover:bg-white/20 text-white'
              }
            >
              Annuel
            </Button>
          </div>
        </div>

        {summary && (
          <>
            {/* Section Revenus */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Revenus
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 text-green-300 text-sm mb-2">
                    <Euro className="w-5 h-5" />
                    Chiffre d'Affaires
                  </div>
                  <div className="text-3xl font-bold text-white">{formatCurrency(summary.revenue.total_ca)}</div>
                  <div className="text-sm text-green-400 mt-1">{summary.revenue.transaction_count} transactions</div>
                </div>
                
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 text-emerald-300 text-sm mb-2">
                    <TrendingUp className="w-5 h-5" />
                    Marge Brute
                  </div>
                  <div className="text-3xl font-bold text-white">{formatCurrency(summary.revenue.total_margin)}</div>
                </div>
                
                <div className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 text-teal-300 text-sm mb-2">
                    <Landmark className="w-5 h-5" />
                    Dépôts Banque
                  </div>
                  <div className="text-3xl font-bold text-white">{formatCurrency(summary.deposits.total)}</div>
                  <div className="text-sm text-teal-400 mt-1">{summary.deposits.count} dépôt(s)</div>
                </div>
              </div>
            </div>

            {/* Section Charges */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Charges & Dépenses
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-blue-300 text-sm mb-2">
                    <Building className="w-4 h-4" />
                    Charges Fixes
                  </div>
                  <div className="text-2xl font-bold text-white">{formatCurrency(summary.charges.fixes)}</div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-purple-300 text-sm mb-2">
                    <ShoppingCart className="w-4 h-4" />
                    Charges Variables
                  </div>
                  <div className="text-2xl font-bold text-white">{formatCurrency(summary.charges.variables)}</div>
                </div>
                
                <div className="bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-red-300 text-sm mb-2">
                    <FileText className="w-4 h-4" />
                    Sorties Caisse
                  </div>
                  <div className="text-2xl font-bold text-white">{formatCurrency(summary.expenses.total)}</div>
                  <div className="text-sm text-red-400 mt-1">{summary.expenses.count} sortie(s)</div>
                </div>
                
                <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 rounded-xl p-5">
                  <div className="flex items-center gap-2 text-orange-300 text-sm mb-2">
                    <TrendingDown className="w-4 h-4" />
                    Total Charges
                  </div>
                  <div className="text-2xl font-bold text-white">{formatCurrency(summary.charges.total + summary.expenses.total)}</div>
                </div>
              </div>
            </div>

            {/* Détail par catégorie */}
            {Object.keys(summary.charges.by_category).length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-semibold text-white/80 mb-3">Détail des charges par catégorie</h3>
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
                  <div className="divide-y divide-white/10">
                    {Object.entries(summary.charges.by_category).map(([name, data]) => (
                      <div key={name} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            data.type === 'fixe' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                          }`}>
                            {data.type === 'fixe' ? 'Fixe' : 'Variable'}
                          </span>
                          <span className="text-white">{name}</span>
                        </div>
                        <span className="font-medium text-white">{formatCurrency(data.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Résultat */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-400" />
                Résultat
              </h2>
              <div className={`rounded-xl p-8 text-center ${
                summary.result.brut >= 0 
                  ? 'bg-gradient-to-br from-green-500/30 to-emerald-600/20 border border-green-500/50' 
                  : 'bg-gradient-to-br from-red-500/30 to-red-600/20 border border-red-500/50'
              }`}>
                <div className={`text-sm mb-2 ${summary.result.brut >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {summary.result.brut >= 0 ? 'Bénéfice' : 'Perte'}
                </div>
                <div className="text-5xl font-bold text-white">
                  {formatCurrency(Math.abs(summary.result.brut))}
                </div>
                <div className="text-sm text-white/60 mt-3">
                  CA ({formatCurrency(summary.revenue.total_ca)}) - Charges ({formatCurrency(summary.charges.total)}) - Sorties ({formatCurrency(summary.expenses.total)})
                </div>
              </div>
            </div>

            {/* Statut paiement charges */}
            {summary.charges.unpaid > 0 && (
              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/30 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <div className="font-medium text-yellow-300">Charges non payées</div>
                    <div className="text-sm text-yellow-400">{formatCurrency(summary.charges.unpaid)} restant à payer</div>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/monthly-charges')}
                  size="sm"
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Voir les charges
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
